import type { TFunction } from 'i18next'

import {
  calculateRemainingPayment,
  formatTimeRemaining,
} from 'entities/kyrgyzstan/lib'

import type {
  CancellationPenaltyData,
  KyrgyzstanOrderData,
} from 'shared/api/kyrgyzstan/orders'
import type { RealtyObject } from 'shared/api/kyrgyzstan/search'
import { DATE_FORMAT_LIST } from 'shared/constants/dateFormatList'
import { dateFormat } from 'shared/lib/dateFormat'
import { priceShow } from 'shared/lib/priceShow'

interface BookingDetail {
  label: string
  value: string
}

interface PaymentDetail {
  label: string
  value: string
  isLineThrough?: boolean
}

const formatPriceWithCurrency = (price: number, currency: string): string =>
  `${priceShow(String(price))} ${currency}`

export const getBookingDetails = (
  order: KyrgyzstanOrderData | null | undefined,
  realtyData: RealtyObject | null | undefined,
  t: TFunction,
): BookingDetail[] => {
  if (!order) return []

  return [
    {
      label: t('kyrgyzstan_block.cancel_booking.voucher_number'),
      value: order.voucher_code ?? '-',
    },
    {
      label: t('kyrgyzstan_block.cancel_booking.checkin_date'),
      value:
        order.start_date && realtyData?.times?.arrival_time
          ? `${dateFormat(order.start_date, DATE_FORMAT_LIST.FULL_DATE)} с ${realtyData.times.arrival_time}`
          : '-',
    },
    {
      label: t('kyrgyzstan_block.cancel_booking.accommodation'),
      value: order.realty_object.name ?? '-',
    },
    {
      label: t('kyrgyzstan_block.cancel_booking.time_remaining'),
      value:
        formatTimeRemaining(
          order.start_date,
          realtyData?.times?.arrival_time,
        ) ?? '-',
    },
  ]
}

export const getPaymentDetails = (
  order: KyrgyzstanOrderData | null | undefined,
  cancellationPenalty: CancellationPenaltyData | null | undefined,
  t: TFunction,
): PaymentDetail[] => {
  if (!order || !cancellationPenalty) return []

  const firstRowLabelKey =
    order.payment_status === 'full'
      ? 'kyrgyzstan_block.cancel_booking.full_payment'
      : 'kyrgyzstan_block.cancel_booking.prepayment'

  return [
    {
      label: t(firstRowLabelKey),
      value: priceShow(String(order.payment_amount ?? 0)),
    },
    {
      label: t('kyrgyzstan_block.cancel_booking.payment_on_checkin'),
      value: formatPriceWithCurrency(
        calculateRemainingPayment(
          order.total_price,
          cancellationPenalty.payment_amount,
        ),
        cancellationPenalty.currency_code,
      ),
      isLineThrough: true,
    },
    {
      label: order.is_checkin_within_24h
        ? t('kyrgyzstan_block.cancel_booking.withheld')
        : t('kyrgyzstan_block.cancel_booking.refund'),
      value: formatPriceWithCurrency(
        order.is_checkin_within_24h
          ? cancellationPenalty.penalty_amount
          : cancellationPenalty.refund_amount,
        cancellationPenalty.currency_code,
      ),
    },
  ]
}
